import React, { useContext, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { makeStyles } from '@material-ui/core/styles'
import styles from '../../../../styles'
import classNames from 'classnames'
import { ClassValue } from 'classnames/types'
import { ContractsModel, ContractsOmission } from '../../../../types/contracts/contractsModel'
import { PrelevementContext } from '../PrelevementContext'
import { useMediaQuery } from 'react-responsive'
import PropertyConcerned from '../../../../components/modalStepper/step/step0/PropertyConcerned'
import PrelevementPersonalInfo from './step1/PrelevementPersonalInfo'
import PrelevementFinancialInfo from './step2/PrelevementFinancialInfo'
import PrelevementSummary from './step3/PrelevementSummary'
import { SignResponseModel } from '../../profileModel'
import { EDLProvider } from '../../../dashboard/edl/context/EDLContext'

const useStyle = makeStyles(
	(theme) => (
		{
			...styles(theme),
			stepperLargeScreenStyle: {
				width: '60%',
				maxWidth: 900,
				margin: '25px auto 0'
			},
			stepperSmallScreenStyle: {
				marginTop: 40
			},
			breadcrumbStyle: {
				marginBottom: 55,
				color: theme.palette.blue['30']
			},
			descriptionStyle: {
				marginBottom: 40,

				'& > span:first-child, & img': {
					marginRight: 15
				}
			}
		}
	)
)

interface AcceptingProps {
	contracts: Omit<ContractsModel, ContractsOmission>[],
	step: number,
	startsWithPropertyConcerned: number,
	stepNumber: number,
	signResponse?: SignResponseModel,
	signStatus?: string,
	setSignStatus: (value: string) => void
}

type StepperProps = AcceptingProps

const Stepper: React.FC<StepperProps> = (
	{
		contracts,
		step,
		startsWithPropertyConcerned,
		stepNumber,
		signResponse,
		signStatus,
		setSignStatus
	}
) => {
	const classes = useStyle()
	const { closeDialog, resetStep, nextStep, prelevement } = useContext(PrelevementContext)
	const stepDescriptionId: string = `profile.prelevement.step${startsWithPropertyConcerned}.title`
	const [error, setError] = useState<string | undefined>(undefined)

	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	const descriptionClass: ClassValue = classNames(
		classes.flex,
		classes.descriptionStyle,
		{
			[classes.alignCenter]: isLargeScreen,
			[classes.flex]: !isLargeScreen,
			[classes.flexColumn]: !isLargeScreen
		},
		classes.colorBlue
	)

	const breadcrumbClass: ClassValue = classNames(
		classes.text0,
		classes.breadcrumbStyle
	)

	const stepperClass: ClassValue = classNames(
		{
			[classes.stepperLargeScreenStyle]: isLargeScreen,
			[classes.stepperSmallScreenStyle]: !isLargeScreen
		}
	)

	const updatePropertyConcerned = (contracts: ContractsModel[] | ContractsModel) => {
		prelevement.propertyConcerned = Array.isArray(contracts) ? contracts : [contracts]
		nextStep()
	}

	const getComponent = () => {
		switch (startsWithPropertyConcerned) {
			case 0:
				return <PropertyConcerned
					contractsMapped={contracts}
					updatePropertyConcerned={updatePropertyConcerned}
					hasButton
					defaultSelection={contracts}
					multiSelect
				/>
			case 1:
				return <PrelevementPersonalInfo error={error} setError={setError} />
			case 2:
				return <PrelevementFinancialInfo setError={setError} />
			case 3:
				return <EDLProvider>
					<PrelevementSummary
						signResponse={signResponse}
						closeDialog={closeDialog}
						resetStep={resetStep}
						stepNumber={stepNumber}
						signStatus={signStatus}
						setSignStatus={setSignStatus}
					/>
				</EDLProvider>
			default:
				return <PropertyConcerned
					contractsMapped={contracts}
					updatePropertyConcerned={updatePropertyConcerned}
					hasButton
				/>
		}
	}

	return (
		<div className={stepperClass}>
			{
				startsWithPropertyConcerned < 3 &&
				<>
					{
						isLargeScreen &&
						<div className={breadcrumbClass}>
							{
								startsWithPropertyConcerned >= 1 &&
								<><span>
									<FormattedMessage
										id="profile.prelevement.subHeader.bail"
										defaultMessage="Bail n°"
										description="Bail number"
										values={{
											nbContracts: prelevement.propertyConcerned.length,
											bail: prelevement.propertyConcerned.map(property => property.lease).join(', ')
										}}
									/>
								</span></>
							}
						</div>
					}

					<div className={descriptionClass}>
						<span>
							<span className={classes.text11}>
								{step}
							</span>

							<span className={classes.text11_opacity}>
								/{stepNumber}
							</span>
						</span>

						{
							isLargeScreen &&
							<img src="img/requests/longArrow.svg" alt="Long arrow" />
						}

						<span className={classes.text11}>
							<FormattedMessage
								id={stepDescriptionId}
								defaultMessage="Step description"
								description="Step description"
							/>
						</span>
					</div>
				</>
			}

			<div>
				{
					React.createElement(getComponent)
				}
			</div>
		</div>
	)
}

export default Stepper
